
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { User, BlogPost, Comment, Like, CustomRole, Permission, Testimonial } from '../types';
import { BLOG_POSTS as INITIAL_POSTS, INITIAL_USERS, INITIAL_TESTIMONIALS } from '../constants';
import { db } from '../db';

type Theme = 'light' | 'dark';

interface AppContextType {
  currentUser: User | null;
  registeredUsers: User[];
  posts: BlogPost[];
  comments: Comment[];
  likes: Like[];
  customRoles: CustomRole[];
  testimonials: Testimonial[];
  theme: Theme;
  institutionalCALogo: string;
  isDbLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (name: string, email: string, password: string) => Promise<{ success: boolean; message: string }>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  logout: () => void;
  toggleTheme: () => void;
  addPost: (post: Omit<BlogPost, 'id' | 'date' | 'views' | 'likes' | 'shares' | 'commentCount' | 'author' | 'authorId' | 'status'>) => Promise<void>;
  updatePost: (id: string, post: Partial<BlogPost>) => Promise<void>;
  deletePost: (id: string) => Promise<void>;
  approvePost: (id: string) => Promise<void>;
  updatePostMetrics: (id: string, metrics: { views?: number, likes?: number, shares?: number, commentCount?: number }) => Promise<void>;
  updateUserRole: (userId: string, role: string) => Promise<void>;
  toggleUserVerification: (userId: string) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  addCustomRole: (name: string, permissions: Permission[]) => void;
  deleteCustomRole: (roleId: string) => void;
  hasPermission: (permission: Permission) => boolean;
  incrementViewCount: (id: string) => void;
  addComment: (postId: string, content: string) => Promise<void>;
  approveComment: (commentId: string) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
  toggleLike: (postId: string) => Promise<void>;
  isLiked: (postId: string) => boolean;
  addTestimonial: (testimonial: Omit<Testimonial, 'id'>) => Promise<void>;
  updateTestimonial: (id: string, testimonial: Partial<Testimonial>) => Promise<void>;
  deleteTestimonial: (id: string) => Promise<void>;
  resetToDefaults: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const DEFAULT_ROLES: CustomRole[] = [
  { id: 'admin', name: 'Administrator', permissions: ['CAN_APPROVE_POSTS', 'CAN_MODERATE_COMMENTS', 'CAN_TUNE_METRICS', 'CAN_MANAGE_USERS', 'CAN_WRITE_POSTS', 'CAN_MANAGE_TESTIMONIALS'] },
  { id: 'marketer', name: 'Digital Marketer', permissions: ['CAN_TUNE_METRICS', 'CAN_WRITE_POSTS'] },
  { id: 'guest', name: 'Guest Contributor', permissions: ['CAN_WRITE_POSTS'] }
];

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDbLoading, setIsDbLoading] = useState(true);
  const [registeredUsers, setRegisteredUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [likes, setLikes] = useState<Like[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>(DEFAULT_ROLES);
  const [theme, setTheme] = useState<Theme>('dark');

  // One-time Database Initialization
  useEffect(() => {
    const init = async () => {
      setIsDbLoading(true);
      await db.initialize();
      const [u, p, c, l, t, r, serverTheme] = await Promise.all([
        db.users.getAll(),
        db.posts.getAll(),
        db.comments.getAll(),
        db.likes.getAll(),
        db.testimonials.getAll(),
        db.roles.getAll().catch(() => [] as CustomRole[]),
        db.settings.getTheme().catch(() => 'dark' as Theme)
      ]);
      
      setRegisteredUsers(u);
      setPosts(p);
      setComments(c);
      setLikes(l);
      setTestimonials(t);

      // Roles: if none persisted yet, store defaults
      if (Array.isArray(r) && r.length > 0) {
        setCustomRoles(r);
      } else {
        setCustomRoles(DEFAULT_ROLES);
        await db.roles.saveAll(DEFAULT_ROLES);
      }

      setTheme(serverTheme);

      const savedUser = localStorage.getItem('mp_active_session');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        const match = u.find(user => user.id === parsed.id);
        if (match) setCurrentUser(match);
      }
      setIsDbLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const institutionalCALogo = useMemo(() => {
    const admin = registeredUsers.find(u => u.isAdmin || u.id === 'admin-1');
    return admin?.caLogo || 'https://upload.wikimedia.org/wikipedia/commons/b/b9/CA_Logo_India.png';
  }, [registeredUsers]);

  const hasPermission = (permission: Permission): boolean => {
    if (!currentUser) return false;
    if (currentUser.isAdmin) return true;
    const role = customRoles.find(r => r.id === currentUser.role);
    return role ? role.permissions.includes(permission) : false;
  };

  const login = async (email: string, password: string) => {
    const user = await db.users.findByEmail(email);
    if (user && user.password === password) {
      setCurrentUser(user);
      localStorage.setItem('mp_active_session', JSON.stringify({ id: user.id }));
      return { success: true, message: 'Institutional Session Initialized' };
    }
    return { success: false, message: 'Access Denied: Invalid Protocols' };
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('mp_active_session');
  };

  const register = async (name: string, email: string, password: string) => {
    const existing = await db.users.findByEmail(email);
    if (existing) return { success: false, message: 'Node already exists in repository' };
    
    const isAdmin = email.toLowerCase().trim() === 'manthan@ca.com';
    const newUser: User = { 
      id: `u-${Date.now()}`, 
      name: name.trim(), 
      email: email.toLowerCase().trim(), 
      password: password.trim(), 
      isAdmin,
      isVerifiedMember: false,
      role: isAdmin ? 'admin' : 'guest'
    };
    
    const updated = [...registeredUsers, newUser];
    setRegisteredUsers(updated);
    await db.users.saveAll(updated);
    setCurrentUser(newUser);
    localStorage.setItem('mp_active_session', JSON.stringify({ id: newUser.id }));
    return { success: true, message: 'Entity Provisioned Successfully' };
  };

  const updateProfile = async (updates: Partial<User>) => {
    if (!currentUser) return;
    const updatedUser = { ...currentUser, ...updates };
    setCurrentUser(updatedUser);
    const updatedAll = registeredUsers.map(u => u.id === currentUser.id ? updatedUser : u);
    setRegisteredUsers(updatedAll);
    await db.users.saveAll(updatedAll);
  };

  const addPost = async (postData: any) => {
    const newPost: BlogPost = {
      ...postData,
      id: `post-${Date.now()}`,
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      views: 0,
      likes: 0,
      shares: 0,
      commentCount: 0,
      author: currentUser?.name || 'Guest',
      authorId: currentUser?.id || 'guest',
      status: hasPermission('CAN_APPROVE_POSTS') ? 'published' : 'pending'
    };
    const updated = [newPost, ...posts];
    setPosts(updated);
    await db.posts.saveAll(updated);
  };

  const updatePost = async (id: string, data: Partial<BlogPost>) => {
    const updated = posts.map(p => p.id === id ? { ...p, ...data } : p);
    setPosts(updated);
    await db.posts.saveAll(updated);
  };

  const deletePost = async (id: string) => {
    const updated = posts.filter(p => p.id !== id);
    setPosts(updated);
    await db.posts.saveAll(updated);
  };

  const approvePost = async (id: string) => {
    const updated: BlogPost[] = posts.map(p => p.id === id ? { ...p, status: 'published' as const } : p);
    setPosts(updated);
    await db.posts.saveAll(updated);
  };

  const updatePostMetrics = async (id: string, metrics: any) => {
    const updated = posts.map(p => p.id === id ? { ...p, ...metrics } : p);
    setPosts(updated);
    await db.posts.saveAll(updated);
  };

  const incrementViewCount = async (id: string) => {
    const updated = posts.map(p => p.id === id ? { ...p, views: p.views + 1 } : p);
    setPosts(updated);
    await db.posts.saveAll(updated);
  };

  const addComment = async (postId: string, content: string) => {
    if (!currentUser) return;
    const newComment: Comment = {
      id: `c-${Date.now()}`,
      postId, userId: currentUser.id, userName: currentUser.name,
      content, date: 'Just now', status: 'pending'
    };
    const updated = [newComment, ...comments];
    setComments(updated);
    await db.comments.saveAll(updated);
    
    const postUpdate = posts.map(p => p.id === postId ? { ...p, commentCount: p.commentCount + 1 } : p);
    setPosts(postUpdate);
    await db.posts.saveAll(postUpdate);
  };

  const toggleLike = async (postId: string) => {
    if (!currentUser) return;
    const existing = likes.findIndex(l => l.postId === postId && l.userId === currentUser.id);
    let newLikes = [...likes];
    if (existing > -1) {
      newLikes.splice(existing, 1);
      const postUpdate = posts.map(p => p.id === postId ? { ...p, likes: Math.max(0, p.likes - 1) } : p);
      setPosts(postUpdate);
      await db.posts.saveAll(postUpdate);
    } else {
      newLikes.push({ postId, userId: currentUser.id });
      const postUpdate = posts.map(p => p.id === postId ? { ...p, likes: p.likes + 1 } : p);
      setPosts(postUpdate);
      await db.posts.saveAll(postUpdate);
    }
    setLikes(newLikes);
    await db.likes.saveAll(newLikes);
  };

  const isLiked = (postId: string) => !!likes.find(l => l.postId === postId && l.userId === currentUser?.id);

  const addTestimonial = async (data: Omit<Testimonial, 'id'>) => {
    const newT = { id: `t-${Date.now()}`, ...data };
    const updated = [newT, ...testimonials];
    setTestimonials(updated);
    await db.testimonials.saveAll(updated);
  };

  const deleteTestimonial = async (id: string) => {
    const updated = testimonials.filter(t => t.id !== id);
    setTestimonials(updated);
    await db.testimonials.saveAll(updated);
  };

  const updateUserRole = async (uId: string, role: string) => {
    const updated = registeredUsers.map(u => u.id === uId ? { ...u, role } : u);
    setRegisteredUsers(updated);
    await db.users.saveAll(updated);
  };

  const toggleUserVerification = async (uId: string) => {
    if (!currentUser?.isAdmin) return;
    const updated = registeredUsers.map(u => u.id === uId ? { ...u, isVerifiedMember: !u.isVerifiedMember } : u);
    setRegisteredUsers(updated);
    await db.users.saveAll(updated);
  };

  const deleteUser = async (uId: string) => {
    const updated = registeredUsers.filter(u => u.id !== uId);
    setRegisteredUsers(updated);
    await db.users.saveAll(updated);
  };

  const toggleTheme = async () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    // Persist server-side (best-effort)
    await db.settings.setTheme(next).catch(() => undefined);
  };
  const resetToDefaults = async () => {
    // Restore the same defaults previously used for localStorage seeding
    await Promise.all([
      db.users.saveAll(INITIAL_USERS),
      db.posts.saveAll(INITIAL_POSTS),
      db.testimonials.saveAll(INITIAL_TESTIMONIALS),
      db.comments.saveAll([]),
      db.likes.saveAll([]),
      db.roles.saveAll(DEFAULT_ROLES),
      db.settings.setTheme('dark')
    ]).catch(() => undefined);

    setRegisteredUsers(INITIAL_USERS);
    setPosts(INITIAL_POSTS);
    setTestimonials(INITIAL_TESTIMONIALS);
    setComments([]);
    setLikes([]);
    setCustomRoles(DEFAULT_ROLES);
    setTheme('dark');
    setCurrentUser(null);
    localStorage.removeItem('mp_active_session');
  };

  return (
    <AppContext.Provider value={{
      currentUser, registeredUsers, posts, comments, likes, customRoles, testimonials, theme, institutionalCALogo, isDbLoading,
      login, register, updateProfile, logout, toggleTheme,
      addPost, updatePost, deletePost, approvePost, updatePostMetrics, updateUserRole, toggleUserVerification, deleteUser,
      addCustomRole: (n, p) => {}, deleteCustomRole: (i) => {}, 
      hasPermission, incrementViewCount, addComment, 
      approveComment: async (id) => {}, deleteComment: async (id) => {}, 
      toggleLike, isLiked,
      addTestimonial, updateTestimonial: async (id, d) => {}, deleteTestimonial, resetToDefaults
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within an AppProvider');
  return context;
};
